import { createRoute, Link } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, onMount, Show } from "solid-js";
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  Download,
  Loader2,
  ShieldCheck,
  Smartphone,
  Upload,
} from "@fleet/icons";
import { Card, CardContent, PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { bibleBooks } from "@/utils/bible-books-data";
import {
  type ImportReport,
  importReportToJson,
  type LegacyImportPlan,
  type LegacyIssueCode,
  type LegacyRejectCode,
  planMigrationFromFile,
  type SkipReason,
} from "@bso/shared";
import { EXPORT_DELIVERY } from "@fleet/plugins/export";
import { useKernelService } from "@fleet/kernel";
import { applyImportPlan } from "@/features/legacy-import/apply";
import { markDeviceMigrated, scanDeviceForLegacyData } from "@/features/legacy-import/device";

/**
 * Migrate data from LEGACY Bible Strong app.
 *
 * Launch unblocker: bso is the only product with real users
 * in store, and cannot lose highlights on update.
 *
 * Two paths, single engine (`@bso/shared/legacy-import`):
 *   - **on-device** (Android/desktop): el comando nativo `scan_legacy_v1` lee el
 *     private directory of old app, which survives update
 *     because `applicationId` is the same. Real path for users.
 *   - **by file**: user copies `databases/highlight` (SQLite) and
 *     uploads it. Serves web/desktop and support.
 *
 * Principles: never destructive · idempotent · fails cleanly · streak-safe
 * fabricated (legacy has no timestamps). See `docs/LEGACY_MIGRATION.md`.
 */
function ImportLegacyPage() {
  const { t } = useI18n();
  const session = useSession();
  const drizzle = useDrizzle();
  const delivery = useKernelService(EXPORT_DELIVERY);

  const [fileName, setFileName] = createSignal("");
  const [plan, setPlan] = createSignal<LegacyImportPlan | undefined>();
  const [fromDevice, setFromDevice] = createSignal(false);
  const [deviceAvailable, setDeviceAvailable] = createSignal(false);
  const [error, setError] = createSignal<{ code: LegacyRejectCode; detail: string } | undefined>();
  const [report, setReport] = createSignal<ImportReport | undefined>();
  const [busy, setBusy] = createSignal(false);

  /** Canonical book abbreviation, for transferring reading position. */
  const abbrForBook = (book: number): string | undefined =>
    bibleBooks.find((entry) => entry.id === book)?.abbr;

  /** Actionable message by rejection code: user must know what to do. */
  const rejectMessage = (code: LegacyRejectCode): string => {
    switch (code) {
      case "empty-file": {
        return t(msg`That file is empty.`);
      }
      case "unreadable-sqlite": {
        return t(
          msg`That database could not be read. Copy the file again from your device — it may have been truncated.`,
        );
      }
      case "missing-highlight-tables": {
        return t(
          msg`That SQLite database is not the highlights one. Pick the file named "highlight" from the old app's databases folder.`,
        );
      }
      case "unknown-json": {
        return t(
          msg`That file has no Bible Strong preferences in it. Pick "persist-rootTest5", or the "highlight" database to bring your highlights over.`,
        );
      }
      default: {
        return t(
          msg`We do not recognise that file. The old app stores your highlights in a SQLite database called "highlight"; that is the one to pick.`,
        );
      }
    }
  };

  const issueMessage = (code: LegacyIssueCode): string => {
    switch (code) {
      case "book-out-of-range": {
        return t(msg`Row pointing to a book outside the 66-book canon`);
      }
      case "chapter-or-verse-out-of-range": {
        return t(msg`Row with an out-of-range chapter or verse`);
      }
      case "unknown-color": {
        return t(msg`Highlight whose colour is missing from the palette`);
      }
      default: {
        return t(msg`Row in an unexpected shape`);
      }
    }
  };

  const skipMessage = (reason: SkipReason): string =>
    reason === "already-imported"
      ? t(msg`Already imported`)
      : t(msg`You already highlighted that verse — yours was kept`);

  /** On mount, checks if this device has legacy app data. */
  onMount(() => {
    void (async () => {
      const scan = await scanDeviceForLegacyData();
      setDeviceAvailable(scan.available);
      if (scan.available && scan.plan?.ok && !scan.alreadyMigrated) {
        setPlan(scan.plan.plan);
        setFromDevice(true);
      }
    })();
  });

  const preview = createMemo(() => {
    const current = plan();
    if (!current) {
      return undefined;
    }
    return {
      highlights: current.verseHighlights.length,
      issues: current.issues.length,
      hasPreferences: current.preferences !== undefined,
    };
  });

  const groupedIssues = createMemo(() => {
    const source = report()?.issues ?? plan()?.issues ?? [];
    const counts = new Map<LegacyIssueCode, number>();
    for (const issue of source) {
      counts.set(issue.code, (counts.get(issue.code) ?? 0) + 1);
    }
    return [...counts.entries()].map(([code, count]) => ({ code, count }));
  });

  const groupedSkips = createMemo(() => {
    const counts = new Map<SkipReason, number>();
    for (const entry of report()?.skipped ?? []) {
      counts.set(entry.reason, (counts.get(entry.reason) ?? 0) + 1);
    }
    return [...counts.entries()].map(([reason, count]) => ({ reason, count }));
  });

  const handleFile = (event: Event & { currentTarget: HTMLInputElement }) => {
    const file = event.currentTarget.files?.[0];
    if (!file) {
      return;
    }
    setFileName(file.name);
    setPlan(undefined);
    setError(undefined);
    setReport(undefined);
    setFromDevice(false);

    void (async () => {
      setBusy(true);
      try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        const result = planMigrationFromFile(bytes);
        if (result.ok) {
          setPlan(result.plan);
        } else {
          setError({ code: result.code, detail: result.detail });
        }
      } catch (cause) {
        setError({ code: "unrecognised-file", detail: String(cause) });
      } finally {
        setBusy(false);
      }
    })();
  };

  const runImport = () => {
    const db = drizzle();
    const current = plan();
    const userId = session.userId();
    if (!db || !current || !userId || busy()) {
      return;
    }
    void (async () => {
      setBusy(true);
      try {
        setReport(await applyImportPlan(db, userId, current, abbrForBook));
        if (fromDevice()) {
          // Sentinel: do not propose again. Legacy data is NOT touched.
          await markDeviceMigrated();
        }
      } catch (cause) {
        setError({ code: "unreadable-sqlite", detail: String(cause) });
      } finally {
        setBusy(false);
      }
    })();
  };

  const downloadReport = () => {
    const done = report();
    if (!done) {
      return;
    }
    const target = delivery();
    if (!target) {
      return;
    }
    void target.saveText(
      `bible-strong-migration-${done.generatedAt.slice(0, 10)}.json`,
      importReportToJson(done),
      "application/json",
    );
  };

  return (
    <PageContainer maxWidth="2xl" class="space-y-8" containerClass="scrollbar-thin">
      <div data-testid="import-legacy-page">
        <PageHeader
          title={t(msg`Import from the old app`)}
          description={t(msg`Bring your highlights over from the previous Bible Strong.`)}
          backTo="/profile"
          icon={Upload}
        />
      </div>

      {/* Promesa explícita: la migración nunca destruye nada. */}
      <Card>
        <CardContent class="space-y-3 p-6">
          <div class="flex items-center gap-2 text-primary">
            <ShieldCheck class="h-5 w-5" />
            <h2 class="text-lg font-bold">{t(msg`Nothing of yours is touched`)}</h2>
          </div>
          <ul class="space-y-2 text-sm text-muted-foreground">
            <li>
              {t(
                msg`Nothing is deleted or overwritten. If you already highlighted a verse, your colour is kept.`,
              )}
            </li>
            <li>{t(msg`Running the import twice does not create duplicates.`)}</li>
            <li>{t(msg`The old app is left untouched, so your data stays there as a backup.`)}</li>
            <li>
              {t(
                msg`The old app did not record dates, so imported highlights do not count towards your reading streak.`,
              )}
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Vía on-device (Tauri) */}
      <Show when={deviceAvailable() && fromDevice()}>
        <Card class="border-primary/30">
          <CardContent class="flex items-center gap-3 p-6" data-testid="import-legacy-device-found">
            <Smartphone class="h-6 w-6 shrink-0 text-primary" />
            <p class="text-sm">
              {t(msg`We found data from the previous Bible Strong on this device.`)}
            </p>
          </CardContent>
        </Card>
      </Show>

      {/* Vía manual por fichero */}
      <Card>
        <CardContent class="space-y-4 p-6">
          <label
            for="legacy-import-file"
            class="flex cursor-pointer flex-col items-center gap-3 rounded-2xl border border-dashed border-border/40 p-8 text-center transition-colors hover:bg-secondary/20"
          >
            <Database class="h-8 w-8 text-muted-foreground" />
            <span class="text-sm font-semibold">
              {t(msg`Or pick the "highlight" database copied from your old device`)}
            </span>
            <span class="text-xs text-muted-foreground">
              {t(msg`SQLite database, or the "persist-rootTest5" preferences file`)}
            </span>
            <Show when={fileName()}>
              <span class="text-xs font-mono text-foreground" data-testid="import-legacy-filename">
                {fileName()}
              </span>
            </Show>
          </label>
          <input
            id="legacy-import-file"
            type="file"
            class="sr-only"
            onChange={handleFile}
            data-testid="import-legacy-file-input"
          />
        </CardContent>
      </Card>

      {/* Rechazo del fichero: siempre explícito y accionable */}
      <Show when={error()}>
        {(current) => (
          <Card class="border-destructive/30">
            <CardContent class="space-y-2 p-6" data-testid="import-legacy-error">
              <div class="flex items-center gap-2 text-destructive">
                <AlertTriangle class="h-5 w-5" />
                <h2 class="text-base font-bold">{t(msg`Nothing was imported`)}</h2>
              </div>
              <p class="text-sm text-muted-foreground">{rejectMessage(current().code)}</p>
            </CardContent>
          </Card>
        )}
      </Show>

      {/* Previsualización antes de escribir */}
      <Show when={report() ? undefined : preview()}>
        {(summary) => (
          <Card>
            <CardContent class="space-y-4 p-6" data-testid="import-legacy-preview">
              <h2 class="text-base font-bold">{t(msg`Ready to import`)}</h2>
              <dl class="grid grid-cols-2 gap-3 text-center">
                <div class="rounded-xl bg-secondary/20 p-3">
                  <dt class="text-xs text-muted-foreground">{t(msg`Highlights`)}</dt>
                  <dd class="text-xl font-bold" data-testid="import-legacy-preview-highlights">
                    {summary().highlights}
                  </dd>
                </div>
                <div class="rounded-xl bg-secondary/20 p-3">
                  <dt class="text-xs text-muted-foreground">{t(msg`Settings`)}</dt>
                  <dd class="text-xl font-bold" data-testid="import-legacy-preview-preferences">
                    {summary().hasPreferences ? t(msg`Found`) : t(msg`None`)}
                  </dd>
                </div>
              </dl>
              <Show when={summary().issues > 0}>
                <div class="space-y-1 rounded-xl border border-border/30 p-3">
                  <p class="text-xs font-semibold">
                    {(() => {
                      const count = summary().issues;
                      return t(msg`${count} rows could not be read and will be skipped:`);
                    })()}
                  </p>
                  <ul class="space-y-0.5 text-xs text-muted-foreground">
                    <For each={groupedIssues()}>
                      {(entry) => (
                        <li>
                          {issueMessage(entry.code)} · {entry.count}
                        </li>
                      )}
                    </For>
                  </ul>
                </div>
              </Show>
              <Button
                class="h-12 w-full gap-2 rounded-2xl"
                onClick={runImport}
                disabled={busy() || !drizzle()}
                data-testid="import-legacy-confirm"
              >
                <Show when={!busy()} fallback={<Loader2 class="h-5 w-5 animate-spin" />}>
                  <Upload class="h-5 w-5" />
                </Show>
                {t(msg`Import my highlights`)}
              </Button>
            </CardContent>
          </Card>
        )}
      </Show>

      {/* Resultado */}
      <Show when={report()}>
        {(done) => (
          <Card class="border-primary/30">
            <CardContent class="space-y-4 p-6" data-testid="import-legacy-report">
              <div class="flex items-center gap-2 text-primary">
                <CheckCircle2 class="h-5 w-5" />
                <h2 class="text-base font-bold">{t(msg`Import finished`)}</h2>
              </div>
              <p class="text-sm text-muted-foreground">
                <span data-testid="import-legacy-result-highlights">
                  {done().imported.highlights}
                </span>{" "}
                {t(msg`highlights imported.`)}
              </p>
              <Show when={done().readingPositionApplied}>
                <p class="text-xs text-muted-foreground" data-testid="import-legacy-position">
                  {t(msg`Your last reading position was restored.`)}
                </p>
              </Show>

              <Show when={groupedSkips().length > 0}>
                <div class="space-y-1 rounded-xl border border-border/30 p-3">
                  <p class="text-xs font-semibold">{t(msg`Skipped (your data was kept):`)}</p>
                  <ul class="space-y-0.5 text-xs text-muted-foreground">
                    <For each={groupedSkips()}>
                      {(entry) => (
                        <li data-testid="import-legacy-skip-row">
                          {skipMessage(entry.reason)} · {entry.count}
                        </li>
                      )}
                    </For>
                  </ul>
                </div>
              </Show>

              <Show when={groupedIssues().length > 0}>
                <div class="space-y-1 rounded-xl border border-border/30 p-3">
                  <p class="text-xs font-semibold">{t(msg`Could not be read:`)}</p>
                  <ul class="space-y-0.5 text-xs text-muted-foreground">
                    <For each={groupedIssues()}>
                      {(entry) => (
                        <li>
                          {issueMessage(entry.code)} · {entry.count}
                        </li>
                      )}
                    </For>
                  </ul>
                </div>
              </Show>

              <div class="flex flex-col gap-3 sm:flex-row">
                <Button
                  variant="outline"
                  class="h-12 flex-1 gap-2 rounded-2xl"
                  onClick={downloadReport}
                  data-testid="import-legacy-download-report"
                >
                  <Download class="h-5 w-5" />
                  {t(msg`Download detailed report`)}
                </Button>
                <Link
                  to="/highlights"
                  class="flex h-12 flex-1 items-center justify-center rounded-2xl border border-border/20 text-sm font-semibold text-foreground/80 transition-colors hover:bg-secondary/20"
                  data-testid="import-legacy-go-to-highlights"
                >
                  {t(msg`See my highlights`)}
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </Show>
    </PageContainer>
  );
}

export const profileImportLegacyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile/import-legacy",
  component: ImportLegacyPage,
});
